import React, { useState, useRef, useLayoutEffect } from "react"
import {
  DropdownContainerWrapper,
  DropdownElement,
  DropdownColumn,
  FlexRow,
  FullWidthSection,
  NavLinkGroupWrapper,
  PanelLink,
  HeadingLink,
  Divider,
  NavLinkGroupRowWrapper,
  HeadingTitle,
} from "./dropdown-menu.style"
import { useWindowWidth } from "@react-hook/window-size"

import {
  INavbarSubcategory,
  INavbarLink,
  INavbarCategory,
  hasSubcategoryChildren,
} from "../../../navigation-config"

const DropdownContainer = ({
  title,
  children,
  visible = false,
}: {
  title: string
  children: React.ReactNode
  visible?: boolean
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const windowWidth = useWindowWidth()

  const containerRef = useRef<HTMLDivElement>()
  const dropdownRef = useRef<HTMLDivElement>()
  const [offsetAmount, setOffsetAmount] = useState(0)

  const onLeave = () => {
    setIsExpanded(false)
  }

  const calculateOffset = () => {
    const containerRect = containerRef?.current?.getBoundingClientRect()
    const elementRect = dropdownRef?.current?.getBoundingClientRect()

    const initialPos = containerRect.x + containerRect.width / 2
    const elementWidth = elementRect.width
    const overhangAmount = elementWidth / 2 - initialPos

    if (overhangAmount > 0) {
      setOffsetAmount(50 + overhangAmount)
    } else {
      setOffsetAmount(0)
    }
  }

  const onEnter = () => {
    calculateOffset()
    setIsExpanded(true)
  }

  useLayoutEffect(() => {
    calculateOffset()
  }, [windowWidth])

  return (
    <DropdownContainerWrapper
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      isExpanded={visible || isExpanded}
      ref={containerRef}
    >
      <p>{title}</p>
      <DropdownElement
        offset={offsetAmount}
        ref={dropdownRef}
        visible={visible || isExpanded}
      >
        {children}
      </DropdownElement>
    </DropdownContainerWrapper>
  )
}

const NavLinkGroup = ({
  content,
  columns = 1,
}: {
  content: INavbarLink[]
  columns?: number
}) => {
  return (
    <NavLinkGroupWrapper columns={columns}>
      {content.map((link, idx) => (
        <PanelLink key={idx} to={link.path}>
          {link.icon && <img src={link.icon} alt="" />}
          {link.text}
        </PanelLink>
      ))}
    </NavLinkGroupWrapper>
  )
}

const NavLinkGroupRow = ({ content }: { content: INavbarLink[] }) => {
  return (
    <NavLinkGroupRowWrapper>
      {content.map((link, idx) => (
        <PanelLink key={idx} to={link.path}>
          {link.icon && <img src={link.icon} alt="" />}
          {link.text}
        </PanelLink>
      ))}
    </NavLinkGroupRowWrapper>
  )
}

const NavSubcategoryRow = ({ content }: { content: INavbarSubcategory[] }) => {
  return content.map((subcategory, idx) => (
    <React.Fragment key={idx}>
      {subcategory.heading.path ? (
        <HeadingLink to={subcategory.heading.path}>
          {subcategory.heading.icon && (
            <img src={subcategory.heading.icon} alt="" />
          )}
          {subcategory.heading.text}
        </HeadingLink>
      ) : (
        <HeadingTitle>{subcategory.heading.text}</HeadingTitle>
      )}

      {subcategory.children.length > 0 && (
        <NavLinkGroupRow content={subcategory.children} />
      )}
    </React.Fragment>
  ))
}

export const DropdownMenu = ({ category }: { category: INavbarCategory }) => {
  const subcategoryView = hasSubcategoryChildren(category.children)

  return (
    <DropdownContainer title={category.heading}>
      {category.landingLink && (
        <>
          <DropdownColumn>
            <FlexRow>
              <HeadingLink
                to={category.landingLink.path}
                style={{ margin: "unset" }}
              >
                {category.landingLink.icon && (
                  <img
                    src={category.landingLink.icon}
                    style={{ width: "16px", height: "16px" }}
                    alt=""
                  />
                )}
                {category.landingLink.text}
              </HeadingLink>
            </FlexRow>
          </DropdownColumn>
          <Divider />
        </>
      )}
      {subcategoryView ? (
        <DropdownColumn>
          <NavSubcategoryRow content={category.children} />
        </DropdownColumn>
      ) : (
        <DropdownColumn>
          <NavLinkGroup
            content={category.children}
            columns={category?.columns?.lg ?? 1}
          />
        </DropdownColumn>
      )}
      {category.moreLinks && (
        <FullWidthSection>
          {category.moreLinks.map((link, idx) => (
            <PanelLink
              to={link.path}
              key={idx}
              style={{
                fontWeight: 500,
                color: "var(--deep-cyan)",
              }}
            >
              {link.icon && (
                <img
                  src={link.icon}
                  style={{ width: "16px", height: "16px" }}
                  alt=""
                />
              )}
              {link.text}
            </PanelLink>
          ))}
        </FullWidthSection>
      )}
    </DropdownContainer>
  )
}

export default DropdownMenu
